import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  deleteTreasuryMovement,
  getTreasurySettings,
  listTreasuryAccounts,
  listTreasuryMovementCategories,
  listTreasuryMovements,
  saveTreasuryMovementsBatch,
} from "../api/cierresTesoreriaApi";
import { getCalendarGrid } from "../../../../shared/utils/calendarUtils";
import {
  amountInputValue,
  displayDateForCell,
  evaluateAmountExpression,
  fmtDMY,
  fmtYMD,
  formatMoney,
  isMovementRowVisible,
  parseMoney,
  removeAccents,
  resolveDateWithinPeriod,
  ymdToDate,
  sortMovementRows,
} from "../utils/lienzoUtils";

const COLUMNS = ["date", "movementType", "categoryId", "concept", "amount", "accountId", "status", "comment"];
const EMPTY_ROWS = 8;
const MOVEMENT_GRID_COLUMNS = "92px 160px 210px 1.4fr 130px 210px 130px 96px 44px";
const MOVEMENT_GRID_MIN_WIDTH = 1246;
const DATE_RANGE_MESSAGE = "La fecha no coincide con el rango seleccionado.";
const FUTURE_CLOSURE_MESSAGE = "El cierre no puede completarse antes de su fecha final.";
const REFERENCE_CACHE_TTL_MS = 60_000;

const referenceRequestCache = new Map();
const pendingMovementRequests = new Map();

const MOVEMENT_OPTIONS = [
  { value: "income", label: "Ingreso" },
  { value: "expense", label: "Egreso" },
  { value: "transfer", label: "Transferencia" },
];

const STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmado" },
  { value: "pending", label: "Pendiente" },
  { value: "cancelled", label: "Cancelado" },
];

const MOVEMENT_FILTER_SELECT_SX = {
  height: 34,
  bgcolor: "white",
  borderRadius: "10px",
  fontSize: 13,
  "& .MuiSelect-select": {
    py: 0.65,
    fontSize: 13,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const MOVEMENT_FILTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": {
        minHeight: 34,
        fontSize: 13,
      },
    },
  },
};

function getTreasuryCacheKey() {
  try {
    return localStorage.getItem("sabi_empresa_id") || "default";
  } catch {
    return "default";
  }
}

function loadCachedReferenceData(cacheName, loader) {
  const key = `${getTreasuryCacheKey()}:${cacheName}`;
  const cached = referenceRequestCache.get(key);
  const now = Date.now();

  if (cached?.data && cached.expiresAt > now) {
    return Promise.resolve(cached.data);
  }
  if (cached?.promise) return cached.promise;

  const promise = loader()
    .then((data) => {
      referenceRequestCache.set(key, {
        data,
        expiresAt: Date.now() + REFERENCE_CACHE_TTL_MS,
      });
      return data;
    })
    .catch((error) => {
      referenceRequestCache.delete(key);
      throw error;
    });

  referenceRequestCache.set(key, { promise });
  return promise;
}

function loadMovementReferenceData() {
  return loadCachedReferenceData("movement-catalogs", () =>
    Promise.all([
      listTreasuryMovementCategories({ isActive: false }),
      listTreasuryAccounts({ includeHistorical: true }),
    ])
  );
}

function loadTreasurySettingsCached() {
  return loadCachedReferenceData("settings", getTreasurySettings);
}

function loadTreasuryMovementsOnce(periodId) {
  const key = `${getTreasuryCacheKey()}:${periodId}`;
  const pending = pendingMovementRequests.get(key);
  if (pending) return pending;

  const request = listTreasuryMovements(periodId).finally(() => {
    if (pendingMovementRequests.get(key) === request) {
      pendingMovementRequests.delete(key);
    }
  });
  pendingMovementRequests.set(key, request);
  return request;
}

function getOptionLabel(options, value) {
  const option = options.find((item) => String(item.value || item.id) === String(value));
  return option?.label || option?.name || "";
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

function focusCellNode(node) {
  if (!node) return;
  node.focus?.();
  if (typeof node.scrollIntoView === "function") {
    node.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }
}

function todayWithinPeriod(period) {
  const today = fmtYMD(new Date());
  if (today >= period.periodStart && today <= period.periodEnd) return today;
  return period.periodStart;
}

function formatDateForCell(value) {
  return fmtDMY(ymdToDate(value));
}

function isRowBlank(row) {
  return (
    !row.id &&
    !row.categoryId &&
    !String(row.concept || "").trim() &&
    !row.amountInput &&
    !row.accountId &&
    !String(row.comment || "").trim()
  );
}

function isDirtyRow(row) {
  return row.rowState === "new" || row.rowState === "edited" || row.rowState === "error";
}

function makeEmptyRow(index, period, defaultMovementType) {
  const date = todayWithinPeriod(period);
  return {
    clientId: `empty-${index}-${Date.now()}`,
    id: "",
    date,
    movementType: defaultMovementType || "income",
    categoryId: "",
    concept: "",
    amountInput: "",
    lastValidAmountInput: "",
    accountId: "",
    status: "confirmed",
    comment: "",
    transferId: null,
    clientTransferId: null,
    transferDirection: null,
    rowState: "empty",
    errors: {},
  };
}

function makeEmptyRows(period, defaultMovementType, count = EMPTY_ROWS) {
  return Array.from({ length: count }, (_, i) => makeEmptyRow(i, period, defaultMovementType));
}

function movementToRow(movement) {
  const amountVal = amountInputValue(movement.amount);
  return {
    clientId: movement.clientId || `mov-${movement.id || Date.now()}`,
    id: movement.id || "",
    date: movement.date || "",
    movementType: movement.movementType || "income",
    categoryId: movement.categoryId || "",
    categoryMissing: Boolean(movement.categoryMissing),
    concept: movement.concept || "",
    amountInput: amountVal,
    lastValidAmountInput: amountVal,
    accountId: movement.accountId || "",
    status: movement.status || "confirmed",
    comment: movement.comment || "",
    transferId: movement.transferId || null,
    clientTransferId: movement.clientTransferId || null,
    transferDirection: movement.transferDirection || null,
    rowState: "unchanged",
    errors: {},
  };
}

function cloneRows(rowsToClone) {
  return rowsToClone.map((row) => ({
    ...row,
    errors: { ...(row.errors || {}) },
  }));
}

function categoryMatchesMovement(category, movementType) {
  if (!category) return true;
  const type = String(category.type || "").toLowerCase();
  if (type === "both") return true;
  return type === String(movementType || "").toLowerCase();
}

function validateRow(row, period, categories, accounts) {
  const errors = {};
  const date = resolveDateWithinPeriod(row.date, period, row.date);
  const evalRes = evaluateAmountExpression(row.amountInput);
  const isTransfer = row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);
  const category = categories.find((item) => String(item.id) === String(row.categoryId));
  const account = accounts.find((item) => String(item.id) === String(row.accountId));

  if (!date || date < period.periodStart || date > period.periodEnd) {
    errors.date = DATE_RANGE_MESSAGE;
  }
  if (!row.movementType) errors.movementType = "Seleccioná un movimiento.";

  if (!isTransfer) {
    if (!row.categoryId) errors.categoryId = "Seleccioná una categoría.";
    if (category && !categoryMatchesMovement(category, row.movementType)) {
      errors.categoryId = "La categoría no coincide con el movimiento.";
    }
    if (!String(row.concept || "").trim()) errors.concept = "El concepto es obligatorio.";
  }

  if (!evalRes.success) {
    errors.amount = evalRes.error;
  }
  if (!row.accountId || !account) errors.accountId = "Seleccioná una cuenta.";
  if (!row.status) errors.status = "Seleccioná un estado.";

  return errors;
}

function getRowErrorItems(error) {
  return error?.data?.rows || error?.response?.data?.rows || [];
}

function cellSx(row, field, isSelected, isDisabledCell) {
  const hasError = Boolean(row.errors?.[field] || (field === "concept" && row.errors?.row));
  const isTransfer =
    row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);

  return {
    px: 1,
    py: 0.55,
    minHeight: 42,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "1px solid rgba(0,0,0,.06)",
    borderLeft: field === "date" ? "5px solid" : "none",
    borderLeftColor:
      field === "date"
        ? row.rowState === "empty"
          ? "transparent"
          : isTransfer
            ? "#1976d2"
            : row.movementType === "income"
              ? "#1ca65c"
              : "#d22c2c"
        : "transparent",
    bgcolor: hasError
      ? "rgba(255, 235, 238, 0.9)"
      : isSelected
        ? "secondary.50"
        : isDisabledCell
          ? "neutral.100"
          : isTransfer
            ? "rgba(25, 118, 210, 0.03)"
            : "transparent",
    outline: isSelected ? "1px solid rgba(14, 50, 149, 0.28)" : "none",
    outlineOffset: "-2px",
    cursor: isDisabledCell ? "default" : "cell",
    verticalAlign: "middle",
  };
}

function CalendarPopover({ anchorEl, value, period, onClose, onPick, onNavigate }) {
  const [cursor, setCursor] = useState(() => ymdToDate(value || period.periodStart) || new Date());
  const days = useMemo(
    () => getCalendarGrid(new Date(cursor.getFullYear(), cursor.getMonth(), 1)),
    [cursor]
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{ role: "dialog", "aria-label": "Seleccionar fecha" }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          event.stopPropagation();
          onNavigate?.(event.key === "ArrowLeft" ? -1 : 1);
        }
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ p: 1.5, width: 260 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
            ‹
          </Button>
          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "primary.900" }}>
            {cursor.toLocaleDateString("es-AR", { month: "long", year: "numeric" })}
          </Typography>
          <Button size="small" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
            ›
          </Button>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.4 }}>
          {["L", "M", "M", "J", "V", "S", "D"].map((day, idx) => (
            <Typography key={`${day}-${idx}`} sx={{ fontSize: 10, textAlign: "center", color: "neutral.600" }}>
              {day}
            </Typography>
          ))}
          {days.map((day) => {
            const disabled = day.ymd < period.periodStart || day.ymd > period.periodEnd;
            const selected = day.ymd === value;
            return (
              <Box
                key={day.ymd}
                onClick={() => {
                  if (!disabled) onPick(day.ymd);
                }}
                sx={{
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: selected ? 900 : 600,
                  borderRadius: "6px",
                  color: disabled ? "neutral.400" : selected ? "white" : "primary.900",
                  bgcolor: selected ? "primary.900" : "transparent",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: day.type === "current" ? 1 : 0.45,
                  "&:hover": disabled ? {} : { bgcolor: selected ? "primary.800" : "secondary.100" },
                }}
              >
                {day.label}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Popover>
  );
}

const CellSelect = memo(function CellSelect({
  rowIndex,
  field,
  row,
  options,
  editable,
  disabledRow,
  selected,
  registerCellRef,
  onSelectCell,
  onBlurCell,
  onKeyDown,
  onUpdateRow,
  onConfirm,
}) {
  const [open, setOpen] = useState(false);
  const isDisabled = disabledRow;

  const currentVal = row[field];
  const displayLabel = useMemo(() => getOptionLabel(options, currentVal), [options, currentVal]);

  const availableOptions = useMemo(() => {
    if (field !== "movementType") return options;
    if (row.id) {
      if (row.movementType === "transfer") {
        return options.filter((item) => item.value === "transfer");
      }
      return options.filter((item) => item.value !== "transfer");
    }
    return options;
  }, [field, options, row.id, row.movementType]);

  if (!editable) {
    return (
      <Typography
        sx={{
          width: "100%",
          fontSize: 14,
          fontWeight: 700,
          color: "primary.900",
          textAlign: "center",
        }}
      >
        {displayLabel || "-"}
      </Typography>
    );
  }

  const handleKeyDownInternal = (event) => {
    if ((event.key === "ArrowUp" || event.key === "ArrowDown") && open) {
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();
      if (open) {
        setOpen(false);
      }
      onKeyDown(event, rowIndex, field);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      if (open) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      return;
    }

    onKeyDown(event, rowIndex, field);
  };

  return (
    <Select
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={row[field] || ""}
      disabled={isDisabled}
      inputRef={(node) => registerCellRef(rowIndex, field, node)}
      onFocus={() => onSelectCell(rowIndex, field)}
      onBlur={(event) => {
        onBlurCell?.(rowIndex, field, field, event.target.value);
      }}
      SelectDisplayProps={{ onKeyDown: handleKeyDownInternal }}
      onChange={(event) => {
        const nextValue = event.target.value;
        onUpdateRow(rowIndex, field, nextValue);
        setOpen(false);
        // Con mouse no forzamos navegación mientras el Popover está cerrando.
        // La confirmación con Enter se resuelve en el MenuItem de abajo.
      }}
      size="small"
      variant="standard"
      disableUnderline
      fullWidth
      sx={{
        "& .MuiSelect-select": {
          py: 0,
          px: 0.5,
          fontSize: 14,
          fontWeight: 700,
          color: field === "movementType" && row.movementType === "transfer" ? "#1976d2" : "primary.900",
          textAlign: "center",
        },
      }}
    >
      {availableOptions.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              onKeyDown(event, rowIndex, field);
            } else if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.stopPropagation();
              onUpdateRow(rowIndex, field, option.value);
              setOpen(false);
              if (field === "status") setTimeout(() => onConfirm?.(option.value), 50);
              else onConfirm?.(option.value);
            } else if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            }
          }}
          sx={{ fontSize: 13, fontWeight: 700, color: option.value === "transfer" ? "#1976d2" : "inherit" }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

const CellAutocomplete = memo(function CellAutocomplete({
  rowIndex,
  field,
  row,
  options,
  editable,
  disabledRow,
  selected,
  registerCellRef,
  onSelectCell,
  onBlurCell,
  onKeyDown,
  onUpdateRow,
  onConfirm,
}) {
  const [open, setOpen] = useState(false);

  const currentOption = useMemo(
    () => options.find((item) => String(item.id) === String(row[field])) || null,
    [options, row, field]
  );
  const [inputValue, setInputValue] = useState(
    currentOption ? currentOption.name || currentOption.label || "" : ""
  );

  useEffect(() => {
    setInputValue(currentOption ? currentOption.name || currentOption.label || "" : "");
  }, [currentOption]);

  const filtered = useMemo(() => {
    const raw = removeAccents(String(inputValue || "")).trim();
    if (!raw) return options;
    return options.filter((item) =>
      removeAccents(String(item.name || item.label || "")).includes(raw)
    );
  }, [options, inputValue]);

  const filterOptions = useCallback(
    (opts, state) => {
      const input = removeAccents(String(state.inputValue || "")).trim();
      if (!input) return opts;
      return opts.filter((item) =>
        removeAccents(String(item.name || item.label || "")).includes(input)
      );
    },
    []
  );

  if (!editable) {
    return (
      <Typography
        sx={{
          width: "100%",
          fontSize: 14,
          fontWeight: 700,
          color: "primary.900",
          textAlign: "center",
        }}
      >
        {currentOption ? currentOption.name || currentOption.label || "-" : "-"}
      </Typography>
    );
  }

  const handleKeyDownInternal = (event) => {
    if ((event.key === "ArrowUp" || event.key === "ArrowDown") && open) {
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      onKeyDown(event, rowIndex, field);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setInputValue(currentOption ? currentOption.name || currentOption.label || "" : "");
      return;
    }

    if (event.key === "Enter" && event.shiftKey && open) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      if (open && filtered.length === 0) {
        event.preventDefault();
        event.stopPropagation();
        onKeyDown(
          {
            ...event,
            customError:
              field === "categoryId"
                ? "Seleccioná una categoría válida de la lista."
                : "Seleccioná una cuenta válida de la lista.",
          },
          rowIndex,
          field
        );
        return;
      }
      if (open) {
        const normalizedInput = removeAccents(String(inputValue || "")).trim();
        const exactOption = filtered.find(
          (option) =>
            removeAccents(String(option.name || option.label || "")).trim() === normalizedInput
        );
        if (exactOption) {
          event.preventDefault();
          event.stopPropagation();
          onUpdateRow(rowIndex, field, exactOption.id);
          setInputValue(exactOption.name || exactOption.label || "");
          setOpen(false);
          onConfirm?.();
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      return;
    }

    onKeyDown(event, rowIndex, field);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={(option) => option?.name || option?.label || ""}
      value={currentOption}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input" || reason === "clear") {
          setInputValue(newInputValue);
        }
      }}
      onChange={(event, newValue) => {
        const newId = newValue ? newValue.id : "";
        onUpdateRow(rowIndex, field, newId);
        setInputValue(newValue ? newValue.name || newValue.label || "" : "");
        setOpen(false);
        onConfirm?.();
      }}
      onBlur={() => {
        setOpen(false);
        if (currentOption) {
          setInputValue(currentOption.name || currentOption.label || "");
        } else {
          setInputValue("");
        }
        onBlurCell?.(rowIndex, field, field, inputValue);
      }}
      filterOptions={filterOptions}
      disabled={disabledRow}
      freeSolo={false}
      disableClearable
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(node) => {
            if (typeof params.inputRef === "function") {
              params.inputRef(node);
            } else if (params.inputRef && typeof params.inputRef === "object") {
              params.inputRef.current = node;
            }
            registerCellRef(rowIndex, field, node);
          }}
          onFocus={() => onSelectCell(rowIndex, field)}
          onKeyDown={handleKeyDownInternal}
          variant="standard"
          fullWidth
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            sx: {
              fontSize: 14,
              fontWeight: 700,
              color: "primary.900",
              p: 0,
              "& .MuiInputBase-input": {
                py: 0,
                px: 0.5,
                fontSize: 14,
                fontWeight: 700,
                textAlign: "center",
              },
            },
          }}
        />
      )}
    />
  );
});

const MovementRow = memo(function MovementRow({
  row,
  rowIndex,
  selectedField,
  categoryOptions,
  accountOptions,
  editable,
  disabledRow,
  period,
  registerCellRef,
  onSelectCell,
  onBlurCell,
  onKeyDown,
  onUpdateRow,
  onOpenCalendar,
  onOpenComment,
  onDeleteRow,
  navigateCell,
  sharedTransferStatus = false,
}) {
  const selected = (field) => editable && selectedField === field;
  const isTransfer = row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);
  const isInbound = isTransfer && row.transferDirection === "INBOUND";

  const renderError = (field) => {
    const message = row.errors?.[field];
    if (!message) return null;
    return (
      <Typography sx={{ fontSize: 10.5, color: "error.700", fontWeight: 700, mt: 0.2 }}>
        {message}
      </Typography>
    );
  };

  const renderTextCell = (field, props = {}) => {
    const cellField = props.cellField || field;
    const value = props.displayValue ?? row[field] ?? "";
    const isDisabledCell = disabledRow || props.disabled;

    if (!editable || isDisabledCell) {
      return (
        <Typography
          sx={{
            width: "100%",
            fontSize: 14,
            fontWeight: 700,
            color: props.color || "primary.900",
            textAlign: props.align || "center",
            overflowWrap: "anywhere",
          }}
        >
          {value || "-"}
        </Typography>
      );
    }

    return (
      <TextField
        inputRef={(node) => registerCellRef(rowIndex, cellField, node)}
        value={value}
        disabled={isDisabledCell}
        onFocus={() => onSelectCell(rowIndex, cellField)}
        onBlur={(event) => {
          onBlurCell?.(rowIndex, field, cellField, event.target.value, event.target);
        }}
        onKeyDown={(event) => onKeyDown(event, rowIndex, cellField)}
        onChange={(event) => onUpdateRow(rowIndex, field, event.target.value)}
        size="small"
        variant="standard"
        fullWidth
        InputProps={{ disableUnderline: true }}
        sx={{
          "& input": {
            p: 0,
            fontSize: 14,
            fontWeight: 700,
            color: props.color || "primary.900",
            textAlign: props.align || "center",
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: props.color || "primary.900",
          },
        }}
      />
    );
  };

  const amountColor = isTransfer
    ? isInbound
      ? "#159251"
      : "#c91f1f"
    : row.movementType === "income"
      ? "#159251"
      : "#c91f1f";

  const evalAmount = evaluateAmountExpression(row.amountInput);
  const displayAmountVal = selected("amount")
    ? row.amountInput
    : evalAmount.success
      ? formatMoney(evalAmount.value)
      : row.amountInput;

  return (
    <Box
      data-movement-row="true"
      data-row-state={row.rowState}
      data-selected-field={editable && selectedField ? selectedField : undefined}
      data-transfer-direction={isTransfer ? row.transferDirection : undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: MOVEMENT_GRID_COLUMNS,
        minWidth: MOVEMENT_GRID_MIN_WIDTH,
        opacity: disabledRow ? 0.55 : 1,
        bgcolor: isTransfer ? "rgba(25, 118, 210, 0.02)" : "transparent",
        "&:hover": {
          bgcolor: disabledRow ? "transparent" : isTransfer ? "rgba(25, 118, 210, 0.05)" : "rgba(0,0,0,.03)",
        },
      }}
    >
      {/* Fecha */}
      <Box sx={cellSx(row, "date", selected("date"), !editable || isInbound)}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.4}>
          {renderTextCell("date", {
            displayValue: displayDateForCell(row.date, row.date || period?.periodStart),
            disabled: isInbound,
          })}
          {editable && !isInbound ? (
            <IconButton
              size="small"
              disabled={disabledRow}
              onClick={(event) => onOpenCalendar(rowIndex, event.currentTarget)}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                  onOpenCalendar(rowIndex, null);
                  onKeyDown(event, rowIndex, "date");
                }
              }}
              title="Seleccionar fecha"
              sx={{ width: 24, height: 24 }}
            >
              <CalendarMonthIcon sx={{ fontSize: 17 }} />
            </IconButton>
          ) : null}
        </Stack>
        {renderError("date")}
      </Box>

      {/* Tipo de movimiento */}
      <Box sx={cellSx(row, "movementType", selected("movementType"), !editable)}>
        <CellSelect
          rowIndex={rowIndex}
          field="movementType"
          row={row}
          options={MOVEMENT_OPTIONS}
          editable={editable}
          disabledRow={disabledRow}
          selected={selected("movementType")}
          registerCellRef={registerCellRef}
          onSelectCell={onSelectCell}
          onBlurCell={onBlurCell}
          onKeyDown={onKeyDown}
          onUpdateRow={onUpdateRow}
          onConfirm={(nextType) => {
            // Este cambio puede agregar o quitar la fila INBOUND. Elegimos el
            // destino con el nuevo tipo y dejamos que el efecto de foco corra
            // cuando React ya haya renderizado la nueva estructura.
            if (nextType === "transfer") {
              onSelectCell(rowIndex, "amount");
            } else if (isTransfer) {
              onSelectCell(rowIndex, "categoryId");
            } else {
              navigateCell?.(rowIndex, "movementType", 1);
            }
          }}
        />
        {renderError("movementType")}
      </Box>

      {/* Categoría */}
      <Box sx={cellSx(row, "categoryId", selected("categoryId"), !editable || isTransfer)}>
        {isTransfer ? (
          <Typography sx={{ width: "100%", fontSize: 14, fontWeight: 700, color: "neutral.500", textAlign: "center" }}>
            —
          </Typography>
        ) : (
          <CellAutocomplete
            rowIndex={rowIndex}
            field="categoryId"
            row={row}
            options={categoryOptions}
            editable={editable}
            disabledRow={disabledRow}
            selected={selected("categoryId")}
            registerCellRef={registerCellRef}
            onSelectCell={onSelectCell}
            onBlurCell={onBlurCell}
            onKeyDown={onKeyDown}
            onUpdateRow={onUpdateRow}
            onConfirm={() => navigateCell?.(rowIndex, "categoryId", 1)}
          />
        )}
        {renderError("categoryId")}
      </Box>

      {/* Concepto */}
      <Box sx={cellSx(row, "concept", selected("concept"), !editable || isTransfer)}>
        {isTransfer ? (
          <Typography sx={{ width: "100%", fontSize: 13, fontWeight: 700, color: "primary.900", textAlign: "left" }}>
            {row.concept || (isInbound ? "Transferencia recibida" : "Transferencia enviada")}
          </Typography>
        ) : (
          renderTextCell("concept", { align: "left" })
        )}
        {renderError("concept")}
        {renderError("row")}
      </Box>

      {/* Monto */}
      <Box sx={cellSx(row, "amount", selected("amount"), !editable || isInbound)}>
        {renderTextCell("amountInput", {
          displayValue: displayAmountVal,
          color: amountColor,
          align: "right",
          cellField: "amount",
          disabled: isInbound,
        })}
        {renderError("amount")}
      </Box>

      {/* Cuenta */}
      <Box sx={cellSx(row, "accountId", selected("accountId"), !editable)}>
        <CellAutocomplete
          rowIndex={rowIndex}
          field="accountId"
          row={row}
          options={accountOptions}
          editable={editable}
          disabledRow={disabledRow}
          selected={selected("accountId")}
          registerCellRef={registerCellRef}
          onSelectCell={onSelectCell}
          onBlurCell={onBlurCell}
          onKeyDown={onKeyDown}
          onUpdateRow={onUpdateRow}
          onConfirm={() => navigateCell?.(rowIndex, "accountId", 1)}
        />
        {renderError("accountId")}
      </Box>

      {/* Estado */}
      <Box
        sx={{
          ...cellSx(row, "status", selected("status"), !editable || (isTransfer && !sharedTransferStatus)),
          ...(isTransfer
            ? {
                bgcolor: "rgba(25, 118, 210, 0.06)",
                borderBottomColor: sharedTransferStatus ? "transparent" : "neutral.300",
              }
            : {}),
        }}
      >
        {!isTransfer || sharedTransferStatus ? (
          <>
            <CellSelect
              rowIndex={rowIndex}
              field="status"
              row={row}
              options={STATUS_OPTIONS}
              editable={editable}
              disabledRow={disabledRow}
              selected={selected("status")}
              registerCellRef={registerCellRef}
              onSelectCell={onSelectCell}
              onBlurCell={onBlurCell}
              onKeyDown={onKeyDown}
              onUpdateRow={onUpdateRow}
              onConfirm={() => navigateCell?.(rowIndex, "status", 1)}
            />
            {renderError("status")}
          </>
        ) : null}
      </Box>

      {/* Comentario */}
      <Box sx={cellSx(row, "comment", selected("comment"), !editable || isInbound)}>
        {!isInbound ? (
          <Stack direction="row" justifyContent="center">
            <IconButton
              ref={(node) => registerCellRef(rowIndex, "comment", node)}
              size="small"
              disabled={!editable && !row.comment}
              onClick={(event) => onOpenComment(event, rowIndex, row.comment)}
              onFocus={() => {
                if (editable) onSelectCell(rowIndex, "comment");
              }}
              onKeyDown={(event) => {
                if (editable) onKeyDown(event, rowIndex, "comment");
              }}
              title={row.comment ? "Ver comentario" : "Sin comentario"}
              sx={{ color: row.comment ? "primary.900" : "neutral.500" }}
            >
              {row.comment ? (
                <ChatBubbleIcon fontSize="small" />
              ) : (
                <ChatBubbleOutlineIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        ) : null}
        {renderError("comment")}
      </Box>

      {/* Acciones / Eliminar */}
      <Box sx={cellSx(row, "actions", false, !editable || isInbound)}>
        {!isInbound && editable && (row.id || !isRowBlank(row)) ? (
          <Stack direction="row" justifyContent="center">
            <IconButton
              size="small"
              onClick={() => onDeleteRow(row, rowIndex)}
              title={isTransfer ? "Eliminar transferencia entre cuentas" : "Eliminar movimiento"}
              sx={{ color: "error.main", width: 28, height: 28 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
});

export default function MovimientosCierreTable({
  period,
  readonly,
  completing = false,
  navigation,
  hideHeader = false,
  initialFilters,
  onComplete,
  onDirtyChange,
  onSaved,
}) {
  const [settings, setSettings] = useState({ defaultMovementType: "income" });
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [movementFilters, setMovementFilters] = useState({
    movementType: initialFilters?.movementType || "",
    categoryId: initialFilters?.categoryId || "",
    status: initialFilters?.status || "",
    accountId: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(() =>
    readonly ? { rowIndex: null, field: null } : { rowIndex: 0, field: "date" }
  );
  const [hoveredTransferGroupKey, setHoveredTransferGroupKey] = useState(null);
  const [calendarCell, setCalendarCell] = useState(null);
  const [commentCell, setCommentCell] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [sessionOverrideIds, setSessionOverrideIds] = useState(() => new Set());
  const cellRefs = useRef({});
  const lastSelectedCellRef = useRef({ rowIndex: 0, field: "date" });
  const loadedPeriodIdRef = useRef(null);
  const initialFocusPeriodIdRef = useRef(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const editable = !readonly;
  const dirtyRowEntries = useMemo(
    () =>
      rows
        .map((row, rowIndex) => ({ row, rowIndex }))
        .filter(({ row }) => isDirtyRow(row)),
    [rows]
  );
  const dirtyRows = useMemo(
    () => dirtyRowEntries.map(({ row }) => row),
    [dirtyRowEntries]
  );
  const hasDirtyRows = dirtyRows.length > 0;
  const hasActiveFilters = Boolean(
    movementFilters.movementType ||
      movementFilters.categoryId ||
      movementFilters.status ||
      movementFilters.accountId
  );
  const firstBlankRowIndex = useMemo(
    () => rows.findIndex((row) => !row.id && isRowBlank(row)),
    [rows]
  );
  const activeAccounts = useMemo(
    () => accounts.filter((account) => account.isActive),
    [accounts]
  );
  const accountById = useMemo(
    () => new Map(accounts.map((account) => [String(account.id), account])),
    [accounts]
  );
  const activeCategoriesByMovement = useMemo(
    () => ({
      income: categories.filter(
        (category) => category.isActive && categoryMatchesMovement(category, "income")
      ),
      expense: categories.filter(
        (category) => category.isActive && categoryMatchesMovement(category, "expense")
      ),
    }),
    [categories]
  );
  const categoryById = useMemo(
    () => new Map(categories.map((category) => [String(category.id), category])),
    [categories]
  );
  const visibleRows = useMemo(
    () =>
      rows
        .map((row, rowIndex) => ({ row, rowIndex }))
        .filter(({ row, rowIndex }) =>
          isMovementRowVisible({
            row,
            rowIndex,
            filters: movementFilters,
            editable,
            firstBlankRowIndex,
            sessionOverrideIds,
            allRows: rows,
          })
        ),
    [rows, movementFilters, firstBlankRowIndex, editable, sessionOverrideIds]
  );
  const visibleRowGroups = useMemo(() => {
    const groups = [];
    for (let index = 0; index < visibleRows.length; index += 1) {
      const current = visibleRows[index];
      const transferKey = current.row.transferId || current.row.clientTransferId;
      const next = visibleRows[index + 1];
      const nextTransferKey = next?.row.transferId || next?.row.clientTransferId;

      if (
        transferKey &&
        current.row.transferDirection === "OUTBOUND" &&
        nextTransferKey === transferKey &&
        next.row.transferDirection === "INBOUND"
      ) {
        // La fila OUTBOUND conserva su clientId al cambiar de tipo. Usarlo en
        // la key evita desmontar el Select activo mientras MUI procesa el
        // mouseDown/blur que convirtió la fila en transferencia.
        groups.push({ key: `row-${current.row.clientId}`, isTransfer: true, entries: [current, next] });
        index += 1;
      } else {
        groups.push({ key: `row-${current.row.clientId}`, isTransfer: false, entries: [current] });
      }
    }
    return groups;
  }, [visibleRows]);

  const canEditRow = useCallback(
    (rowIndex) =>
      editable && (firstBlankRowIndex < 0 || rowIndex <= firstBlankRowIndex),
    [editable, firstBlankRowIndex]
  );

  const getOptionsForCategory = useCallback(
    (row) => {
      if (row.movementType === "transfer") return [];
      const filtered = activeCategoriesByMovement[row.movementType] || [];
      const currentCategory = categoryById.get(String(row.categoryId));
      if (
        currentCategory &&
        !filtered.some((item) => String(item.id) === String(currentCategory.id))
      ) {
        return [currentCategory, ...filtered];
      }
      return filtered;
    },
    [activeCategoriesByMovement, categoryById]
  );

  const getOptionsForAccount = useCallback(
    (row) => {
      let availableAccounts = activeAccounts;
      const transferKey = row.transferId || row.clientTransferId;
      if ((row.movementType === "transfer" || Boolean(transferKey)) && transferKey) {
        const twinRow = rows.find(
          (r) => (r.transferId || r.clientTransferId) === transferKey && r.clientId !== row.clientId
        );
        if (twinRow?.accountId) {
          availableAccounts = activeAccounts.filter((a) => String(a.id) !== String(twinRow.accountId));
        }
      }

      const currentAccount = accountById.get(String(row.accountId));
      if (
        currentAccount &&
        !availableAccounts.some((item) => String(item.id) === String(currentAccount.id)) &&
        !(
          transferKey &&
          rows.some(
            (candidate) =>
              (candidate.transferId || candidate.clientTransferId) === transferKey &&
              candidate.clientId !== row.clientId &&
              String(candidate.accountId) === String(currentAccount.id)
          )
        )
      ) {
        return [currentAccount, ...availableAccounts];
      }
      return availableAccounts;
    },
    [accountById, activeAccounts, rows]
  );

  useEffect(() => {
    onDirtyChange?.(hasDirtyRows);
  }, [hasDirtyRows, onDirtyChange]);

  useEffect(() => {
    if (!readonly) return;
    setCalendarCell(null);
    setConfirmCompleteOpen(false);
    setSaving(false);
    setSelectedCell({ rowIndex: null, field: null });
    lastSelectedCellRef.current = { rowIndex: null, field: null };
    initialFocusPeriodIdRef.current = null;
    setMovementFilters((current) =>
      current.status ? { ...current, status: "" } : current
    );
  }, [readonly]);

  useEffect(() => {
    if (!readonly || !initialFilters) return;
    setMovementFilters({
      movementType: initialFilters.movementType || "",
      categoryId: initialFilters.categoryId || "",
      status: initialFilters.status || "",
      accountId: initialFilters.accountId || "",
    });
  }, [readonly, initialFilters]);

  useEffect(() => {
    let alive = true;
    const periodContext = {
      id: period.id,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
    };

    (async () => {
      loadedPeriodIdRef.current = null;
      setLoading(true);
      setError("");
      setSessionOverrideIds(new Set());

      let defaultMovementType = "income";
      loadTreasurySettingsCached()
        .then((nextSettings) => {
          defaultMovementType = nextSettings.defaultMovementType;
          if (!alive) return;
          setSettings(nextSettings);
          setRows((prev) =>
            prev.map((row) =>
              row.rowState === "empty" && isRowBlank(row)
                ? { ...row, movementType: nextSettings.defaultMovementType }
                : row
            )
          );
        })
        .catch((err) => {
          console.warn(
            "No se pudieron obtener las configuraciones de tesorería, usando valores predeterminados.",
            err
          );
        });

      try {
        const [[nextCategories, nextAccounts], movements] = await Promise.all([
          loadMovementReferenceData(),
          loadTreasuryMovementsOnce(period.id),
        ]);
        if (!alive) return;
        const savedRows = sortMovementRows(movements.map(movementToRow));
        loadedPeriodIdRef.current = period.id;

        setCategories(nextCategories);
        setAccounts(nextAccounts);
        setRows(
          readonly
            ? cloneRows(savedRows)
            : [
                ...cloneRows(savedRows),
                ...makeEmptyRows(periodContext, defaultMovementType),
              ]
        );
      } catch (err) {
        if (!alive) return;
        setError(errorToMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      onDirtyChange?.(false);
    };
  }, [period.id, period.periodStart, period.periodEnd, readonly, onDirtyChange]);

  const handleDeleteRow = useCallback(
    async (row, rowIndex) => {
      if (!editable || saving || loading) return;

      const transferKey = row.transferId || row.clientTransferId;
      const isTransfer = row.movementType === "transfer" || Boolean(transferKey);

      if (!row.id) {
        setRows((prev) => {
          if (isTransfer && transferKey) {
            return prev.filter((r) => (r.transferId || r.clientTransferId) !== transferKey);
          }
          return prev.filter((_, idx) => idx !== rowIndex);
        });
        setSessionOverrideIds((prev) => {
          if (!prev.has(row.clientId)) return prev;
          const next = new Set(prev);
          next.delete(row.clientId);
          return next;
        });
        return;
      }

      const hasOtherPendingChanges = rowsRef.current.some(
        (candidate) =>
          isDirtyRow(candidate) &&
          String(candidate.clientId || candidate.id) !== String(row.clientId || row.id) &&
          (!transferKey || (candidate.transferId || candidate.clientTransferId) !== transferKey)
      );
      if (hasOtherPendingChanges) {
        setError("Guardá los demás cambios antes de eliminar un movimiento.");
        return;
      }

      const confirmMsg = isTransfer
        ? "¿Querés eliminar esta transferencia entre cuentas? Se eliminarán ambos impactos."
        : "¿Querés eliminar este movimiento?";

      if (!window.confirm(confirmMsg)) return;

      try {
        setLoading(true);
        setError("");
        await deleteTreasuryMovement(period.id, row.id);
        setToast("Operación eliminada correctamente.");

        const movements = await listTreasuryMovements(period.id);
        const savedRows = sortMovementRows(movements.map(movementToRow));
        setRows(
          readonly
            ? cloneRows(savedRows)
            : [
                ...cloneRows(savedRows),
                ...makeEmptyRows(period, settings.defaultMovementType),
              ]
        );
        onSaved?.({ periodId: period.id });
      } catch (err) {
        setError(errorToMessage(err) || "No se pudo eliminar el movimiento.");
      } finally {
        setLoading(false);
      }
    },
    [
      editable,
      loading,
      onSaved,
      period,
      readonly,
      saving,
      settings.defaultMovementType,
    ]
  );

  const guardarTrabajoRef = useRef(null);
  const visibleRowsRef = useRef(visibleRows);
  visibleRowsRef.current = visibleRows;

  const validateCell = useCallback(
    (rowIndex, field) => {
      const row = rowsRef.current[rowIndex];
      if (!row) return { valid: true, error: "" };
      const isTransfer = row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);

      if (field === "date") {
        const resolved = resolveDateWithinPeriod(row.date, period, row.date);
        if (!resolved || resolved < period.periodStart || resolved > period.periodEnd) {
          return { valid: false, error: DATE_RANGE_MESSAGE };
        }
      } else if (field === "movementType") {
        if (!row.movementType) return { valid: false, error: "Seleccioná un movimiento." };
      } else if (field === "categoryId") {
        if (!isTransfer) {
          if (!row.categoryId) return { valid: false, error: "Seleccioná una categoría." };
          const category = categoryById.get(String(row.categoryId));
          if (category && !categoryMatchesMovement(category, row.movementType)) {
            return { valid: false, error: "La categoría no coincide con el movimiento." };
          }
        }
      } else if (field === "concept") {
        if (!isTransfer && !String(row.concept || "").trim()) {
          return { valid: false, error: "El concepto es obligatorio." };
        }
      } else if (field === "amount" || field === "amountInput") {
        const evalRes = evaluateAmountExpression(row.amountInput);
        if (!evalRes.success) {
          return { valid: false, error: evalRes.error, code: evalRes.code };
        }
      } else if (field === "accountId") {
        if (!row.accountId) return { valid: false, error: "Seleccioná una cuenta." };
        const account = accountById.get(String(row.accountId));
        if (!account) return { valid: false, error: "Seleccioná una cuenta." };
      } else if (field === "status") {
        if (!row.status) return { valid: false, error: "Seleccioná un estado." };
      }

      return { valid: true, error: "" };
    },
    [period, categoryById, accountById]
  );

  const navigateCell = useCallback((currentRowIndex, currentField, direction, wrapRows = true) => {
    const vRows = visibleRowsRef.current;
    const vIndex = vRows.findIndex((item) => item.rowIndex === currentRowIndex);
    if (vIndex < 0) return;

    const currentRow = vRows[vIndex].row;
    const isTransfer = currentRow.movementType === "transfer" || Boolean(currentRow.transferId) || Boolean(currentRow.clientTransferId);
    const isInbound = isTransfer && currentRow.transferDirection === "INBOUND";

    const cIndex = COLUMNS.indexOf(currentField);
    if (cIndex < 0) return;

    let nextVIndex = vIndex;
    let nextCIndex = cIndex + direction;

    if (isTransfer) {
      if (direction > 0) {
        if (!isInbound) {
          if (currentField === "date") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("movementType"); }
          else if (currentField === "movementType") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("amount"); }
          else if (currentField === "amount") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("accountId"); }
          else if (currentField === "accountId") { nextVIndex = vIndex + 1; nextCIndex = COLUMNS.indexOf("accountId"); }
          else if (currentField === "status") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("comment"); }
          else if (currentField === "comment") { nextVIndex = vIndex + 2; nextCIndex = COLUMNS.indexOf("date"); }
        } else {
          if (currentField === "accountId") { nextVIndex = vIndex - 1; nextCIndex = COLUMNS.indexOf("status"); }
        }
      } else {
        if (!isInbound) {
          if (currentField === "comment") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("status"); }
          else if (currentField === "status") { nextVIndex = vIndex + 1; nextCIndex = COLUMNS.indexOf("accountId"); }
          else if (currentField === "accountId") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("amount"); }
          else if (currentField === "amount") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("movementType"); }
          else if (currentField === "movementType") { nextVIndex = vIndex; nextCIndex = COLUMNS.indexOf("date"); }
          else if (currentField === "date") { nextVIndex = vIndex - 1; nextCIndex = COLUMNS.indexOf("comment"); }
        } else {
          if (currentField === "accountId") { nextVIndex = vIndex - 1; nextCIndex = COLUMNS.indexOf("accountId"); }
        }
      }
    }

    if (!wrapRows && (nextCIndex < 0 || nextCIndex >= COLUMNS.length)) {
      return;
    }

    if (nextCIndex >= COLUMNS.length) {
      nextCIndex = 0;
      nextVIndex = vIndex + 1;
    } else if (nextCIndex < 0) {
      nextCIndex = COLUMNS.length - 1;
      nextVIndex = vIndex - 1;
    }

    if (nextVIndex >= vRows.length) {
      setSelectedCell({ rowIndex: null, field: null });
      setTimeout(() => {
        focusCellNode(guardarTrabajoRef.current);
      }, 0);
      return;
    }

    if (nextVIndex < 0) {
      nextVIndex = 0;
      nextCIndex = 0;
    }

    const targetRow = vRows[nextVIndex].row;
    const targetRowIndex = vRows[nextVIndex].rowIndex;
    let targetField = COLUMNS[nextCIndex];

    if ((targetRow.movementType === "transfer" || Boolean(targetRow.clientTransferId) || Boolean(targetRow.transferId)) && targetRow.transferDirection === "INBOUND") {
      targetField = "accountId";
    }

    setSelectedCell({ rowIndex: targetRowIndex, field: targetField });
    setTimeout(() => {
      const node = cellRefs.current[`${targetRowIndex}-${targetField}`];
      focusCellNode(node);
    }, 0);
  }, []);

  const navigateVerticalCell = useCallback(
    (currentRowIndex, currentField, rowDirection) => {
      const vRows = visibleRowsRef.current;
      const currentVIndex = vRows.findIndex((item) => item.rowIndex === currentRowIndex);
      if (currentVIndex < 0) return;

      const canFocusField = ({ row, rowIndex }) => {
        if (!canEditRow(rowIndex)) return false;
        const isTransfer =
          row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);
        if (!isTransfer) return true;
        if (row.transferDirection === "INBOUND") return currentField === "accountId";
        return currentField !== "categoryId" && currentField !== "concept";
      };

      let targetVIndex = currentVIndex + rowDirection;
      while (targetVIndex >= 0 && targetVIndex < vRows.length) {
        const target = vRows[targetVIndex];
        if (canFocusField(target)) {
          setSelectedCell({ rowIndex: target.rowIndex, field: currentField });
          setTimeout(() => {
            focusCellNode(cellRefs.current[`${target.rowIndex}-${currentField}`]);
          }, 0);
          return;
        }
        targetVIndex += rowDirection;
      }
    },
    [canEditRow]
  );

  const updateRow = useCallback(
    (rowIndex, field, value) => {
      if (!canEditRow(rowIndex)) {
        if (editable) setToast("Completá primero la fila anterior disponible.");
        return;
      }

      if (field === "amountInput") {
        if (/[^0-9.,+*/()\s-]/.test(value)) {
          setRows((prev) => {
            const row = prev[rowIndex];
            if (!row) return prev;
            const nextRows = [...prev];
            nextRows[rowIndex] = {
              ...row,
              errors: {
                ...row.errors,
                amount: "La expresión contiene caracteres no permitidos.",
              },
            };
            return nextRows;
          });
          return;
        }
      }

      const editedClientId = rowsRef.current[rowIndex]?.clientId;
      if (editedClientId) {
        setSessionOverrideIds((prev) =>
          prev.has(editedClientId) ? prev : new Set(prev).add(editedClientId)
        );
      }

      setRows((prev) => {
        const currentRow = prev[rowIndex];
        if (!currentRow) return prev;

        const isTransfer = currentRow.movementType === "transfer" || Boolean(currentRow.transferId) || Boolean(currentRow.clientTransferId);

        // 1. Cambio de tipo a/desde Transferencia
        if (field === "movementType") {
          const oldType = currentRow.movementType;
          if (oldType !== "transfer" && value === "transfer") {
            if (currentRow.id) return prev; // Tipo inmutable para guardados
            const clientTransferId = `ct-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            const sourceAccName = accountById.get(String(currentRow.accountId))?.name || "";

            const outRow = {
              ...currentRow,
              movementType: "transfer",
              transferDirection: "OUTBOUND",
              clientTransferId,
              categoryId: "",
              concept: "Transferencia enviada",
              rowState: currentRow.rowState === "empty" ? "new" : "edited",
              errors: {},
            };

            const inRow = {
              clientId: `${clientTransferId}-inbound`,
              id: "",
              date: currentRow.date,
              movementType: "transfer",
              transferDirection: "INBOUND",
              clientTransferId,
              categoryId: "",
              concept: sourceAccName ? `Transferencia desde ${sourceAccName}` : "Transferencia recibida",
              amountInput: currentRow.amountInput,
              lastValidAmountInput: currentRow.lastValidAmountInput,
              accountId: "",
              status: currentRow.status || "confirmed",
              comment: currentRow.comment || "",
              rowState: "new",
              errors: {},
            };

            const nextRows = [...prev];
            nextRows[rowIndex] = outRow;
            nextRows.splice(rowIndex + 1, 0, inRow);
            return nextRows;
          } else if (oldType === "transfer" && value !== "transfer") {
            if (currentRow.id) return prev; // Tipo inmutable para guardados
            const transferKey = currentRow.transferId || currentRow.clientTransferId;
            let nextRows = [...prev];
            if (transferKey) {
              nextRows = nextRows.filter(
                (r) => (r.transferId || r.clientTransferId) !== transferKey || r.clientId === currentRow.clientId
              );
            }
            const updatedIndex = nextRows.findIndex((r) => r.clientId === currentRow.clientId);
            if (updatedIndex >= 0) {
              nextRows[updatedIndex] = {
                ...currentRow,
                movementType: value,
                transferDirection: null,
                clientTransferId: null,
                transferId: null,
                concept: "",
                rowState: currentRow.rowState === "empty" ? "new" : "edited",
                errors: {},
              };
            }
            return nextRows;
          }
        }

        // 2. Sincronización en parejas de transferencia
        const transferKey = currentRow.transferId || currentRow.clientTransferId;
        if (transferKey && isTransfer) {
          const nextRows = [...prev];
          const twinIndex = nextRows.findIndex(
            (r, idx) => idx !== rowIndex && (r.transferId || r.clientTransferId) === transferKey
          );

          let next = {
            ...currentRow,
            [field]: value,
            errors: {
              ...currentRow.errors,
              [field]: "",
              ...(field === "amountInput" ? { amount: "" } : {}),
            },
          };
          if (field === "date") next.date = resolveDateWithinPeriod(value, period, currentRow.date) || value;

          if (twinIndex >= 0) {
            const twinRow = nextRows[twinIndex];
            const isCurrentOutbound = currentRow.transferDirection === "OUTBOUND";

            const outAccId = isCurrentOutbound ? (field === "accountId" ? value : currentRow.accountId) : twinRow.accountId;
            const inAccId = !isCurrentOutbound ? (field === "accountId" ? value : currentRow.accountId) : twinRow.accountId;

            const outAccName = accountById.get(String(outAccId))?.name || "";
            const inAccName = accountById.get(String(inAccId))?.name || "";

            const conceptOut = inAccName ? `Transferencia a ${inAccName}` : "Transferencia enviada";
            const conceptIn = outAccName ? `Transferencia desde ${outAccName}` : "Transferencia recibida";

            let updatedCurrent = {
              ...next,
              concept: isCurrentOutbound ? conceptOut : conceptIn,
            };

            let updatedTwin = {
              ...twinRow,
              concept: isCurrentOutbound ? conceptIn : conceptOut,
            };

            if (field === "date") updatedTwin.date = next.date;
            if (field === "amountInput") {
              updatedTwin.amountInput = value;
              updatedTwin.lastValidAmountInput = value;
            }
            if (field === "status") updatedTwin.status = value;
            if (field === "comment") updatedTwin.comment = value;

            if (field === "accountId") {
              if (isCurrentOutbound && inAccId && String(value) === String(inAccId)) {
                updatedTwin.accountId = "";
                const newOutAccName = accountById.get(String(value))?.name || "";
                updatedCurrent.concept = "Transferencia enviada";
                updatedTwin.concept = newOutAccName ? `Transferencia desde ${newOutAccName}` : "Transferencia recibida";
                updatedCurrent.errors = { ...updatedCurrent.errors, accountId: "" };
                updatedTwin.errors = { ...updatedTwin.errors, accountId: "" };

                setTimeout(() => {
                  const targetRowIndex = visibleRowsRef.current.find((item) => item.row.clientId === twinRow.clientId)?.rowIndex ?? (rowIndex + 1);
                  setSelectedCell({ rowIndex: targetRowIndex, field: "accountId" });
                  const node = cellRefs.current[`${targetRowIndex}-accountId`];
                  focusCellNode(node);
                }, 0);
              } else if (outAccId && inAccId && String(outAccId) === String(inAccId)) {
                updatedCurrent.errors = {
                  ...updatedCurrent.errors,
                  accountId: "La cuenta de origen y destino deben ser distintas.",
                };
              } else {
                updatedCurrent.errors = { ...updatedCurrent.errors, accountId: "" };
                updatedTwin.errors = { ...updatedTwin.errors, accountId: "" };
              }
            }

            if (updatedCurrent.rowState === "unchanged") updatedCurrent.rowState = "edited";
            if (updatedTwin.rowState === "unchanged") updatedTwin.rowState = "edited";

            nextRows[rowIndex] = updatedCurrent;
            nextRows[twinIndex] = updatedTwin;
            return nextRows;
          }
        }

        // 3. Fila regular (income / expense)
        let next = {
          ...currentRow,
          [field]: value,
          errors: {
            ...currentRow.errors,
            [field]: "",
            ...(field === "amountInput" ? { amount: "" } : {}),
          },
        };

        if (field === "date") {
          next.date = resolveDateWithinPeriod(value, period, currentRow.date) || value;
        }
        if (field === "amountInput") next.amountInput = value;
        if (field === "movementType") {
          const currentCategory = categoryById.get(String(currentRow.categoryId));
          if (
            currentCategory &&
            !categoryMatchesMovement(currentCategory, value)
          ) {
            next.categoryId = "";
          }
        }

        if (next.rowState === "empty") next.rowState = "new";
        else if (next.rowState === "unchanged") {
          next.rowState = "edited";
        }

        const nextRows = [...prev];
        nextRows[rowIndex] = next;
        return nextRows;
      });
    },
    [canEditRow, editable, accountById, categoryById, period]
  );

  const handleBlurCell = useCallback(
    (rowIndex, field, cellField, _value, blurredNode) => {
      if (cellField === "amount" || field === "amountInput" || field === "amount") {
        const row = rowsRef.current[rowIndex];
        if (!row) return;
        const refocusAmount = () => {
          setSelectedCell({ rowIndex, field: "amount" });
          focusCellNode(blurredNode);
          setTimeout(() => {
            const node = blurredNode?.isConnected
              ? blurredNode
              : cellRefs.current[`${rowIndex}-amount`];
            focusCellNode(node);
          }, 0);
        };

        if (!String(row.amountInput || "").trim()) {
          if (row.errors?.amount) refocusAmount();
          return;
        }
        const evalRes = evaluateAmountExpression(row.amountInput);
        if (evalRes.success) {
          const formattedInput = amountInputValue(evalRes.value);
          setRows((prev) => {
            const currentRow = prev[rowIndex];
            if (!currentRow) return prev;
            const nextRows = [...prev];
            nextRows[rowIndex] = {
              ...currentRow,
              amountInput: formattedInput,
              lastValidAmountInput: formattedInput,
              errors: { ...currentRow.errors, amount: "" },
            };
            return nextRows;
          });
        } else {
          setRows((prev) => {
            const currentRow = prev[rowIndex];
            if (!currentRow) return prev;
            const nextRows = [...prev];
            nextRows[rowIndex] = {
              ...currentRow,
              amountInput: currentRow.lastValidAmountInput ?? "",
              errors: { ...currentRow.errors, amount: evalRes.error },
            };
            return nextRows;
          });
          refocusAmount();
        }
      }
    },
    []
  );

  const confirmComment = useCallback(() => {
    if (!editable || !commentCell) return;
    updateRow(commentCell.rowIndex, "comment", commentDraft);
    setCommentCell(null);
  }, [commentCell, commentDraft, editable, updateRow]);

  const handleKeyDown = useCallback(
    (event, rowIndex, field) => {
      if (!editable) return;

      if (event.customError) {
        setRows((prev) => {
          const row = prev[rowIndex];
          if (!row) return prev;
          const nextRows = [...prev];
          nextRows[rowIndex] = {
            ...row,
            errors: { ...row.errors, [field]: event.customError },
          };
          return nextRows;
        });
        setTimeout(() => {
          const node = cellRefs.current[`${rowIndex}-${field}`];
          focusCellNode(node);
        }, 0);
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        navigateCell(rowIndex, field, event.key === "ArrowLeft" ? -1 : 1, false);
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        navigateVerticalCell(rowIndex, field, event.key === "ArrowUp" ? -1 : 1);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        const direction = event.shiftKey ? -1 : 1;

        if (field === "date" && !event.shiftKey) {
          const anchorEl = cellRefs.current[`${rowIndex}-date`];
          setCalendarCell({ rowIndex, anchorEl });
          return;
        }

        if (field === "comment") {
          if (event.shiftKey) {
            navigateCell(rowIndex, field, direction);
            return;
          }
          if (!commentCell) {
            const anchorEl = cellRefs.current[`${rowIndex}-comment`];
            setCommentCell({ rowIndex, anchorEl });
            setCommentDraft(rowsRef.current[rowIndex]?.comment || "");
            return;
          } else {
            confirmComment();
            navigateCell(rowIndex, field, direction);
            return;
          }
        }

        const validation = validateCell(rowIndex, field);
        if (!validation.valid) {
          setRows((prev) => {
            const row = prev[rowIndex];
            if (!row) return prev;
            const nextRows = [...prev];
            const isAmount = field === "amount" || field === "amountInput";
            nextRows[rowIndex] = {
              ...row,
              ...(isAmount ? { amountInput: row.lastValidAmountInput ?? "" } : {}),
              errors: { ...row.errors, [field === "amountInput" ? "amount" : field]: validation.error },
            };
            return nextRows;
          });
          setTimeout(() => {
            const node = cellRefs.current[`${rowIndex}-${field}`];
            focusCellNode(node);
          }, 0);
          return;
        }

        if (field === "amount" || field === "amountInput") {
          const evalRes = evaluateAmountExpression(rowsRef.current[rowIndex]?.amountInput);
          if (evalRes.success) {
            const formattedInput = amountInputValue(evalRes.value);
            setRows((prev) => {
              const row = prev[rowIndex];
              if (!row) return prev;
              const nextRows = [...prev];
              nextRows[rowIndex] = {
                ...row,
                amountInput: formattedInput,
                lastValidAmountInput: formattedInput,
                errors: { ...row.errors, amount: "" },
              };
              return nextRows;
            });
          }
        }

        setRows((prev) => {
          const row = prev[rowIndex];
          if (!row || !row.errors?.[field]) return prev;
          const nextRows = [...prev];
          nextRows[rowIndex] = {
            ...row,
            errors: { ...row.errors, [field]: "" },
          };
          return nextRows;
        });

        navigateCell(rowIndex, field, direction);
      } else if (event.key === "Tab") {
        event.preventDefault();
        const direction = event.shiftKey ? -1 : 1;
        navigateCell(rowIndex, field, direction);
      } else if (event.key === "Escape") {
        if (calendarCell) {
          event.preventDefault();
          const targetRowIndex = calendarCell.rowIndex;
          setCalendarCell(null);
          setTimeout(() => {
            focusCellNode(cellRefs.current[`${targetRowIndex}-date`]);
          }, 0);
        } else if (commentCell) {
          event.preventDefault();
          setCommentCell(null);
        }
      }
    },
    [editable, calendarCell, commentCell, confirmComment, validateCell, navigateCell, navigateVerticalCell]
  );

  const registerCellRef = useCallback((rowIndex, field, node) => {
    cellRefs.current[`${rowIndex}-${field}`] = node;
  }, []);

  const selectCell = useCallback((rowIndex, field) => {
    initialFocusPeriodIdRef.current = period.id;
    setSelectedCell((current) =>
      current.rowIndex === rowIndex && current.field === field
        ? current
        : { rowIndex, field }
    );
  }, [period.id]);

  useEffect(() => {
    if (
      !editable ||
      loading ||
      loadedPeriodIdRef.current !== period.id ||
      initialFocusPeriodIdRef.current === period.id
    ) {
      return;
    }

    const firstEditableRow = visibleRows.find(({ rowIndex }) => canEditRow(rowIndex));
    if (!firstEditableRow) return;

    initialFocusPeriodIdRef.current = period.id;
    const rowIndex = firstEditableRow.rowIndex;
    setSelectedCell({ rowIndex, field: "date" });
  }, [canEditRow, editable, loading, period.id, visibleRows]);

  useEffect(() => {
    if (selectedCell.rowIndex != null && selectedCell.field) {
      lastSelectedCellRef.current = selectedCell;
      const node = cellRefs.current[`${selectedCell.rowIndex}-${selectedCell.field}`];
      if (node) {
        focusCellNode(node);
      }
    }
  }, [selectedCell]);

  useEffect(() => {
    if (!editable) return undefined;

    const handleDetachedArrowNavigation = (event) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          "input, textarea, select, [contenteditable='true'], [role='combobox'], [role='listbox'], [role='menu'], [role='dialog'], [role='tab']"
        )
      ) {
        return;
      }

      const { rowIndex, field } = lastSelectedCellRef.current;
      if (rowIndex == null || !field) return;

      event.preventDefault();
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        navigateVerticalCell(rowIndex, field, event.key === "ArrowUp" ? -1 : 1);
      } else {
        navigateCell(rowIndex, field, event.key === "ArrowLeft" ? -1 : 1, false);
      }
    };

    document.addEventListener("keydown", handleDetachedArrowNavigation);
    return () => document.removeEventListener("keydown", handleDetachedArrowNavigation);
  }, [editable, navigateCell, navigateVerticalCell]);

  const openCalendar = useCallback((rowIndex, anchorEl) => {
    setCalendarCell(anchorEl ? { rowIndex, anchorEl } : null);
  }, []);

  const openComment = useCallback(
    (event, rowIndex, comment) => {
      if (!editable && !comment) return;
      setCommentCell({ rowIndex, anchorEl: event.currentTarget });
      setCommentDraft(comment || "");
    },
    [editable]
  );

  const validateDirtyRows = () => {
    if (!editable) return false;
    let valid = true;
    let firstInvalid = null;
    const nextRows = rows.map((row, rowIndex) => {
      if (!isDirtyRow(row)) return row;
      const errors = validateRow(row, period, categories, accounts);
      const hasErr = Object.values(errors).some(Boolean);
      if (hasErr) {
        valid = false;
        if (!firstInvalid) {
          const firstField = COLUMNS.find((col) => errors[col]);
          firstInvalid = { rowIndex, field: firstField || "concept" };
        }
      }
      return { ...row, rowState: hasErr ? "error" : row.rowState, errors };
    });
    setRows(nextRows);
    if (firstInvalid) {
      setSelectedCell(firstInvalid);
      setTimeout(() => {
        const node = cellRefs.current[`${firstInvalid.rowIndex}-${firstInvalid.field}`];
        focusCellNode(node);
      }, 0);
    }
    return valid;
  };

  const buildPayload = () =>
    dirtyRows.map((row) => {
      const isTransfer = row.movementType === "transfer" || Boolean(row.transferId) || Boolean(row.clientTransferId);
      return {
        clientId: String(row.clientId),
        id: row.id || undefined,
        transferId: row.transferId || undefined,
        clientTransferId: row.clientTransferId || undefined,
        transferDirection: row.transferDirection || undefined,
        date: resolveDateWithinPeriod(row.date, period, row.date),
        movementType: isTransfer ? "TRANSFER" : row.movementType === "expense" ? "EXPENSE" : "INCOME",
        categoryId: isTransfer ? null : row.categoryId ? Number(row.categoryId) : null,
        concept: String(row.concept || "").trim(),
        amount: parseMoney(row.amountInput),
        accountId: Number(row.accountId),
        status: row.status,
        comment: row.comment || "",
      };
    });

  const saveDirtyRows = async ({ silent = false } = {}) => {
    if (!editable) return false;
    setSelectedCell({ rowIndex: null, field: null });
    if (!validateDirtyRows()) {
      setError("Revisá las filas marcadas antes de continuar.");
      return false;
    }
    const payload = buildPayload();
    if (!payload.length) return true;

    setSaving(true);
    setError("");
    try {
      const saveResult = await saveTreasuryMovementsBatch(period.id, payload);
      const saved = saveResult.movements || [];
      const savedByClientOrId = new Map();
      saved.forEach((savedMovement) => {
        if (savedMovement.clientId) savedByClientOrId.set(String(savedMovement.clientId), savedMovement);
        if (savedMovement.id) savedByClientOrId.set(String(savedMovement.id), savedMovement);
      });
      const overrideIdRemap = [];
      const nextRows = rows
        .filter((row) => row.rowState !== "empty" && !isRowBlank(row))
        .map((row) => {
          if (!isDirtyRow(row)) return { ...row, rowState: "unchanged", errors: {} };
          const savedMovement = savedByClientOrId.get(String(row.clientId)) || savedByClientOrId.get(String(row.id));
          if (!savedMovement) return { ...row, rowState: "unchanged", errors: {} };
          const savedRow = { ...movementToRow(savedMovement), rowState: "unchanged" };
          overrideIdRemap.push({ oldId: row.clientId, newId: savedRow.clientId });
          return savedRow;
        });
      const sortedNextRows = sortMovementRows(nextRows);
      setRows([...sortedNextRows, ...makeEmptyRows(period, settings.defaultMovementType)]);
      setSessionOverrideIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        overrideIdRemap.forEach(({ oldId, newId }) => {
          if (!prev.has(oldId)) return;
          if (oldId !== newId) {
            next.delete(oldId);
            changed = true;
          }
          if (!next.has(newId)) {
            next.add(newId);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      onSaved?.({
        periodId: period.id,
        derivedData: saveResult.derivedData,
      });
      if (!silent) setToast("Movimientos guardados correctamente.");
      return true;
    } catch (err) {
      const rowErrors = getRowErrorItems(err);
      if (rowErrors.length) {
        let firstErrorCell = null;
        setRows((prev) =>
          prev.map((row, index) => {
            const rowError = rowErrors.find((item) => {
              const batchIndex = Number(item.index);
              return (
                (item.clientId != null &&
                  String(item.clientId) === String(row.clientId)) ||
                (item.id != null && String(item.id) === String(row.id)) ||
                (Number.isInteger(batchIndex) &&
                  dirtyRowEntries[batchIndex]?.rowIndex === index)
              );
            });
            if (!rowError) return row;
            const errField = rowError.field || "concept";
            if (!firstErrorCell) {
              firstErrorCell = { rowIndex: index, field: errField };
            }
            return {
              ...row,
              rowState: "error",
              errors: { ...row.errors, [errField]: rowError.message },
            };
          })
        );
        if (firstErrorCell) {
          setSelectedCell(firstErrorCell);
          setTimeout(() => {
            const node = cellRefs.current[`${firstErrorCell.rowIndex}-${firstErrorCell.field}`];
            focusCellNode(node);
          }, 0);
        }
      }
      setError(errorToMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await saveDirtyRows();
  };

  const handleOpenCompleteConfirmation = async () => {
    if (!editable || saving || completing || loading) return;
    const saved = await saveDirtyRows({ silent: true });
    if (!saved) return;
    setError("");
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (!editable || completing) return;
    if (period.periodEnd > fmtYMD(new Date())) {
      setError(FUTURE_CLOSURE_MESSAGE);
      setConfirmCompleteOpen(false);
      return;
    }

    setError("");
    try {
      await onComplete?.(period.id);
      setRows((prev) =>
        prev
          .filter((row) => row.rowState !== "empty" && !isRowBlank(row))
          .map((row) => ({ ...row, rowState: "unchanged", errors: {} }))
      );
      setConfirmCompleteOpen(false);
    } catch (err) {
      setError(errorToMessage(err));
    }
  };

  const updateMovementFilter = (field, value) => {
    setMovementFilters((prev) => ({ ...prev, [field]: value }));
    setSessionOverrideIds(new Set());
  };

  const clearMovementFilters = () => {
    setMovementFilters({ movementType: "", categoryId: "", status: "", accountId: "" });
    setSessionOverrideIds(new Set());
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: {
          xs: "auto",
          md: hideHeader ? "calc(100vh - 236px)" : "calc(100vh - 170px)",
        },
      }}
    >
      {!hideHeader ? (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
        <Box
          sx={{
            px: { xs: 1.4, sm: 1.8 },
            py: 1.2,
            borderRadius: "16px",
            bgcolor: "rgba(244, 245, 251, 0.88)",
            border: "1px solid rgba(255,255,255,0.8)",
            boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.18)",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "neutral.600", mb: 0.2 }}>
                Cierre seleccionado
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 900,
                  color: "primary.900",
                  lineHeight: 1.2,
                }}
              >
                Cierre {formatDateForCell(period.periodStart)} - {formatDateForCell(period.periodEnd)}
              </Typography>
            </Box>

            <Chip
              label={readonly ? "Completado" : "En trabajo"}
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: "999px",
                bgcolor: readonly ? "rgba(185, 246, 202, 0.72)" : "secondary.100",
                color: "primary.900",
                fontSize: 12,
                fontWeight: 900,
              }}
            />
          </Stack>
        </Box>

        {editable ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
          >
            <Button
              ref={guardarTrabajoRef}
              onClick={handleSave}
              disabled={!hasDirtyRows || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                bgcolor: "primary.500",
                color: "#fff",
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "primary.600" },
                "&.Mui-disabled": {
                  bgcolor: "primary.500",
                  color: "rgba(255, 255, 255, 0.72)",
                  opacity: 0.5,
                },
              }}
            >
              Guardar trabajo
            </Button>
            <Button
              onClick={handleOpenCompleteConfirmation}
              disabled={saving || completing || loading}
              startIcon={
                saving || completing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                bgcolor: "primary.500",
                color: "#fff",
                borderRadius: "33px",
                boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                px: 2.5,
                py: 1,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "primary.600" },
                "&.Mui-disabled": {
                  bgcolor: "primary.500",
                  color: "rgba(255, 255, 255, 0.72)",
                  opacity: 0.5,
                },
              }}
            >
              Completar cierre
            </Button>
          </Stack>
        ) : null}
        </Stack>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: "20px",
          bgcolor: "rgba(255,255,255,0.86)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{navigation}</Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="flex-end"
          >
            {readonly ? (
              <Chip
                icon={<VisibilityIcon sx={{ fontSize: 17 }} />}
                label="Modo lectura"
                size="small"
                sx={{
                  alignSelf: { xs: "flex-start", md: "center" },
                  bgcolor: "rgba(185, 246, 202, 0.72)",
                  color: "primary.900",
                  borderRadius: "999px",
                  fontWeight: 900,
                  "& .MuiChip-label": { fontWeight: 900 },
                }}
              />
            ) : null}

            <Select
              value={movementFilters.movementType}
              onChange={(event) => updateMovementFilter("movementType", event.target.value)}
              displayEmpty
              size="small"
              MenuProps={MOVEMENT_FILTER_MENU_PROPS}
              sx={{ minWidth: { xs: "100%", md: 130 }, ...MOVEMENT_FILTER_SELECT_SX }}
            >
              <MenuItem value="">Movimiento</MenuItem>
              {MOVEMENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={movementFilters.categoryId}
              onChange={(event) => updateMovementFilter("categoryId", event.target.value)}
              displayEmpty
              size="small"
              MenuProps={MOVEMENT_FILTER_MENU_PROPS}
              sx={{ minWidth: { xs: "100%", md: 145 }, ...MOVEMENT_FILTER_SELECT_SX }}
            >
              <MenuItem value="">Categoría</MenuItem>
              {movementFilters.categoryId === "__uncategorized__" ? (
                <MenuItem value="__uncategorized__">Sin categoría</MenuItem>
              ) : null}
              {movementFilters.categoryId &&
              movementFilters.categoryId !== "__uncategorized__" &&
              !categories.some(
                (category) => String(category.id) === String(movementFilters.categoryId)
              ) ? (
                <MenuItem value={movementFilters.categoryId}>Categoría seleccionada</MenuItem>
              ) : null}
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>

            {editable ? (
              <Select
                value={movementFilters.status}
                onChange={(event) => updateMovementFilter("status", event.target.value)}
                displayEmpty
                size="small"
                MenuProps={MOVEMENT_FILTER_MENU_PROPS}
                sx={{ minWidth: { xs: "100%", md: 120 }, ...MOVEMENT_FILTER_SELECT_SX }}
              >
                <MenuItem value="">Estado</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : null}

            <Select
              value={movementFilters.accountId}
              onChange={(event) => updateMovementFilter("accountId", event.target.value)}
              displayEmpty
              size="small"
              MenuProps={MOVEMENT_FILTER_MENU_PROPS}
              sx={{ minWidth: { xs: "100%", md: 140 }, ...MOVEMENT_FILTER_SELECT_SX }}
            >
              <MenuItem value="">Cuenta</MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>

            <Button
              onClick={clearMovementFilters}
              disabled={!hasActiveFilters}
              sx={{
                color: "primary.900",
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 800,
                fontSize: 13,
                minWidth: "auto",
                height: 34,
                px: 1,
              }}
            >
              Limpiar
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 1, borderRadius: "14px", py: 0.4 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            borderRadius: "20px",
            bgcolor: "neutral.50",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box sx={{ overflow: "auto", height: "100%", maxHeight: { xs: 420, md: "none" } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: MOVEMENT_GRID_COLUMNS,
              minWidth: MOVEMENT_GRID_MIN_WIDTH,
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "neutral.600",
            }}
          >
            {["Fecha", "Movimiento", "Categoría", "Concepto", "Monto", "Cuenta", "Estado", "Comentario", ""].map((label, idx) => (
              <Typography
                key={`${label}-${idx}`}
                sx={{
                  px: 1,
                  py: 1.15,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "logoblanco",
                  textAlign: "center",
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {loading ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 5, minWidth: 1050, bgcolor: "neutral.50" }}>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                Cargando movimientos...
              </Typography>
            </Stack>
          ) : visibleRows.length === 0 ? (
            <Stack alignItems="center" spacing={0.7} sx={{ py: 5, minWidth: 1050, bgcolor: "neutral.50" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                No hay movimientos para los filtros seleccionados.
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.600" }}>
                Limpiá los filtros para volver a ver el cierre completo.
              </Typography>
            </Stack>
          ) : (
            visibleRowGroups.map((group) => {
              const isActiveTransfer =
                group.isTransfer &&
                editable &&
                group.entries.some(({ rowIndex }) => selectedCell.rowIndex === rowIndex);
              const isExpandedTransfer =
                group.isTransfer &&
                (isActiveTransfer || hoveredTransferGroupKey === group.key);
              const displayedEntries =
                group.isTransfer && !isExpandedTransfer
                  ? group.entries.slice(0, 1)
                  : group.entries;

              return (
                <Box
                  key={group.key}
                  data-transfer-group={group.isTransfer ? "true" : undefined}
                  data-transfer-expanded={group.isTransfer ? String(isExpandedTransfer) : undefined}
                  onMouseEnter={
                    group.isTransfer ? () => setHoveredTransferGroupKey(group.key) : undefined
                  }
                  onMouseLeave={
                    group.isTransfer ? () => setHoveredTransferGroupKey(null) : undefined
                  }
                  sx={
                    group.isTransfer
                      ? {
                          borderTop: "1px solid rgba(25, 118, 210, 0.55)",
                          borderBottom: "1px solid rgba(25, 118, 210, 0.55)",
                        }
                      : undefined
                  }
                >
                  {displayedEntries.map(({ row, rowIndex }) => (
                    <MovementRow
                      key={row.clientId}
                      row={row}
                      rowIndex={rowIndex}
                      selectedField={
                        selectedCell.rowIndex === rowIndex ? selectedCell.field : ""
                      }
                      categoryOptions={getOptionsForCategory(row)}
                      accountOptions={getOptionsForAccount(row)}
                      editable={editable}
                      disabledRow={editable && !canEditRow(rowIndex)}
                      period={period}
                      registerCellRef={registerCellRef}
                      onSelectCell={selectCell}
                      onBlurCell={handleBlurCell}
                      onKeyDown={handleKeyDown}
                      onUpdateRow={updateRow}
                      onOpenCalendar={openCalendar}
                      onOpenComment={openComment}
                      onDeleteRow={handleDeleteRow}
                      navigateCell={navigateCell}
                      sharedTransferStatus={group.isTransfer && row.transferDirection === "OUTBOUND"}
                    />
                  ))}
                </Box>
              );
            })
          )}
          </Box>
        </Box>
      </Paper>

      {editable ? (
        <CalendarPopover
          anchorEl={calendarCell?.anchorEl}
          value={rows[calendarCell?.rowIndex]?.date}
          period={period}
          onClose={() => setCalendarCell(null)}
          onNavigate={(direction) => {
            const rowIndex = calendarCell?.rowIndex;
            setCalendarCell(null);
            if (rowIndex != null) navigateCell(rowIndex, "date", direction, false);
          }}
          onPick={(date) => {
            updateRow(calendarCell.rowIndex, "date", date);
            setCalendarCell(null);
          }}
        />
      ) : null}

      <Popover
        open={Boolean(commentCell)}
        anchorEl={commentCell?.anchorEl}
        onClose={() => setCommentCell(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { maxWidth: "calc(100vw - 32px)" } }}
      >
        {editable ? (
          <Box sx={{ p: 1.5, width: 280 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "neutral.600", mb: 1 }}>
              Comentario del movimiento
            </Typography>
            <TextField
              autoFocus
              multiline
              minRows={3}
              maxRows={5}
              value={commentDraft}
              onChange={(event) => setCommentDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  const targetRowIdx = commentCell?.rowIndex;
                  confirmComment();
                  if (targetRowIdx != null) {
                    navigateCell(targetRowIdx, "comment", 1);
                  }
                }
              }}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Ingresá un comentario..."
              sx={{ mb: 1.2 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setCommentCell(null)}>
                Cancelar
              </Button>
              <Button size="small" variant="contained" onClick={confirmComment}>
                Guardar
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ p: 1.5, maxWidth: 300 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {commentCell?.comment || "Sin comentario"}
            </Typography>
          </Box>
        )}
      </Popover>

      <Popover
        open={confirmCompleteOpen}
        anchorEl={guardarTrabajoRef.current}
        onClose={() => setConfirmCompleteOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900", mb: 1 }}>
            ¿Completar el cierre de tesorería?
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "neutral.600", mb: 2 }}>
            Al completar el cierre el período pasará a estado completado y no se podrán modificar más sus movimientos ni saldos.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setConfirmCompleteOpen(false)} disabled={completing}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirmComplete}
              disabled={completing}
              startIcon={completing ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Completar
            </Button>
          </Stack>
        </Box>
      </Popover>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
      />
    </Box>
  );
}
